import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse
} from 'axios'
import { AUTH_LOGIN_PATH, BASE_URL } from '@/constants/apiPaths'
import { useAuthStore } from '@/stores/authStore'
import { logOnDev } from '@/assets/js/main.ts'
import router from '@/router'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isAxiosError(value: any): value is AxiosError {
  return typeof value?.response === 'object'
}

export abstract class AbstractApiService {
  protected readonly http: AxiosInstance

  protected constructor(
    protected readonly path: string,
    protected readonly baseURL: string = import.meta.env.VITE_BACKEND ?? '/'
  ) {
    if (path) {
      this.baseURL += path
    }
    this.http = axios.create({
      baseURL: BASE_URL,
      timeout: 30000
      // ... further stuff, e.g. `withCredentials: true`
    })
    this.http.defaults.headers.common['Accept'] =
      'application/json;charset=UTF-8'
    this.http.defaults.headers.common['Content-Type'] =
      'application/json;charset=UTF-8'

    this.http.interceptors.request.use(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (req: any) => {
        if (!req.url?.endsWith(AUTH_LOGIN_PATH)) {
          // set access_token
          req.headers.set(
            'Authorization',
            `Bearer ${localStorage.getItem('access_token')}`
          )
        }
        return req
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        return Promise.reject(error)
      }
    )

    this.http.interceptors.response.use(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (response: any) => {
        return response
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        return Promise.reject(error)
      }
    )
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected createParams(record: Record<string, any>): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams()
    for (const key in record) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const value: any = record[key]
        if (value !== null && value !== undefined) {
          params.append(key, value)
        } else {
          logOnDev(
            `Param key '${key}' was null or undefined and will be ignored`
          )
        }
      }
    }
    return params
  }

  protected handleResponse<T>(response: AxiosResponse<T>): T {
    return response.data
  }

  protected handleError(error: unknown): never {
    if (isAxiosError(error)) {
      // check the response from server
      // @ts-ignore
      const serverMessage = error.response?.data?.message ?? 'Server Error'
      if (error.response && error.response.status === 401) {
        const isLoginRequest = error.config?.url?.endsWith(AUTH_LOGIN_PATH)
        if (isLoginRequest) {
          // 401 during login: surface the server message to the user
          throw serverMessage ?? 'Credenziali errate'
        }
        // expired or invalid token: log out and redirect to login
        useAuthStore().logout()
        router.push('/login')
        throw 'Sessione scaduta, effettua nuovamente il login'
      }
      if (error.response && error.response.status === 400) {
        // @ts-ignore
        throw error.response.data?.message ?? serverMessage
      }
      // other status code
      if (serverMessage) {
        throw serverMessage
      }

      if (error.message) {
        throw error.message
      }
    }

    throw new Error(error as any)
  }

  protected handleErrorResetPassword(error: unknown) {
    return error
  }
}
